import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  GoogleReCaptchaCheckbox,
  useGoogleReCaptcha,
} from "@google-recaptcha/react";

import { ConfigContext, IntlContext, NotificationContext } from "@app/provider";
import { ResultCodes } from "@app/service/resultCodes";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";

export type RecaptchaTokens = {
  recaptchaToken?: string;
  recaptchaV2Token?: string;
};

/**
 * Replays the gated mutation with the supplied tokens. Used both for the
 * silent v3 retry (fresh v3 token) and the v2 fallback (v2 token).
 */
export type RecaptchaRetry = (tokens: RecaptchaTokens) => Promise<void>;

/**
 * Thrown by acquireTokens when v3 cannot issue a token (kill switch off but
 * provider not mounted, script not loaded, empty response, etc.).
 * Callers should catch this and call promoteToV2Challenge to surface the v2
 * checkbox as a graceful fallback.
 */
export class RecaptchaV3UnavailableError extends Error {
  constructor(public readonly reason: string) {
    super(`recaptcha v3 unavailable: ${reason}`);
    this.name = "RecaptchaV3UnavailableError";
    // Required when extending Error under ts-jest / older transpile targets —
    // without this, `instanceof RecaptchaV3UnavailableError` returns false
    // because the prototype chain is lost and callers fall through to the
    // generic error path. See https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, RecaptchaV3UnavailableError.prototype);
  }
}

const messages = defineMessages({
  recaptchaValidationFailed: {
    id: "recaptchaValidationFailed",
    defaultMessage: "Please refresh the page and try again.",
  },
  recaptchaV2ChallengePrompt: {
    id: "recaptchaV2ChallengePrompt",
    defaultMessage: "Please complete the verification below to continue.",
  },
});

export interface UseRecaptchaSubmitOptions {
  /**
   * The reCAPTCHA v3 action to bind tokens to. Must match the server's
   * `recaptcha.v3.expectedAction.*` value for the mutation being protected
   * (e.g., "signup", "verifyEmailCode"). Mismatches are rejected server-side.
   */
  action: string;
  /** Optional className on the v2 widget container. */
  v2ContainerClassName?: string;
  /** Optional test id on the v2 widget container. */
  v2TestId?: string;
}

export interface UseRecaptchaSubmitResult {
  /**
   * Acquire a v3 token for the configured action.
   * - Resolves with empty object when the kill switch is off — server-side
   *   verifyV3 will also no-op so this is safe.
   * - Throws RecaptchaV3UnavailableError when v3 is unreachable (missing
   *   key, script not loaded after timeout, empty token, etc.).
   *   Callers should catch and call promoteToV2Challenge.
   */
  acquireTokens: () => Promise<RecaptchaTokens>;
  /**
   * Inspect a thrown error. If it matches a reCAPTCHA-specific server
   * response code (RECAPTCHA_V2_REQUIRED / RECAPTCHA_VALIDATION_FAILED),
   * handle it (show the v2 widget or notify the user) and return true.
   * Callers should treat `true` as "the hook took over; bail out of your
   * post-submit logic" and `false` as "this is a non-recaptcha error, you
   * own it".
   */
  handleRecaptchaError: (err: unknown, retry: RecaptchaRetry) => boolean;
  /**
   * Render the v2 checkbox and arm the supplied retry closure. Used by
   * callers when acquireTokens throws RecaptchaV3UnavailableError (i.e.,
   * the failure happened client-side before the server could ask for v2).
   * If no v2 site key is configured, surfaces a validation-failed
   * notification instead so the user is never stuck.
   */
  promoteToV2Challenge: (reason: string, retry: RecaptchaRetry) => void;
  /**
   * Clear the v2 challenge UI. Call this on happy-path success so the
   * widget is hidden and a fresh challenge is generated next time.
   */
  resetV2Challenge: () => void;
  /**
   * The v2 widget — render in the page wherever the prompt should appear,
   * or `null` when not in fallback mode. Internally re-keyed on each
   * challenge so stale tokens cannot be resubmitted.
   */
  v2Challenge: ReactElement | null;
  /** True while the v2 widget is shown or its retry is in flight. */
  isV2ChallengeActive: boolean;
  /** True after v2 is completed until the armed retry finishes. */
  v2Submitting: boolean;
  /**
   * Whether reCAPTCHA is enabled in the runtime config. When false, acquireTokens
   * resolves with an empty {} and callers should not wait for a token before
   * proceeding (mirrors the server RecaptchaService kill switch).
   */
  isEnabled: boolean;
  /** Sentinel class exported for `instanceof` checks in callers. */
  RecaptchaV3UnavailableError: typeof RecaptchaV3UnavailableError;
}

const useRecaptchaSubmit = (
  options: UseRecaptchaSubmitOptions
): UseRecaptchaSubmitResult => {
  const { action, v2ContainerClassName, v2TestId } = options;

  const intl = useIntl();
  const { config } = useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);
  const { addNotification } = useContext(NotificationContext);

  const recaptchaConfig = config.recaptcha;
  const recaptchaEnabled = !!recaptchaConfig?.enabled;
  const v3SiteKey = recaptchaConfig?.v3SiteKey;
  const v2SiteKey = recaptchaConfig?.v2SiteKey;

  // executeV3 is the v3 token-issuing function. It is undefined until the
  // GoogleReCaptchaProvider script finishes loading (isLoading=true) or
  // when the provider is bypassed entirely (see RecaptchaProvider).
  const { executeV3, isLoading: recaptchaScriptLoading } = useGoogleReCaptcha();

  // Refs so the waitForRecaptchaScript / acquireTokens closures always see
  // the latest values without needing to be re-created on every render.
  const executeV3Ref = useRef(executeV3);
  const scriptLoadingRef = useRef(recaptchaScriptLoading);
  useEffect(() => {
    executeV3Ref.current = executeV3;
    scriptLoadingRef.current = recaptchaScriptLoading;
  }, [executeV3, recaptchaScriptLoading]);

  const [v2Required, setV2Required] = useState(false);
  const [v2Submitting, setV2Submitting] = useState(false);
  const [pendingV2Retry, setPendingV2Retry] = useState<RecaptchaRetry | null>(
    null
  );
  const [v2CheckboxKey, setV2CheckboxKey] = useState(0);

  // One-shot guard for the silent v3 retry. The first RECAPTCHA_RETRYABLE_FAILURE
  // triggers a single fresh-token retry; if that retry also fails retryably the
  // guard is already set, so we escalate to the v2 challenge instead of looping.
  // Reset on success (resetV2Challenge) and on terminal failure.
  const v3RetryUsed = useRef(false);

  // Synchronous re-entry guard for the v2 onChange callback. grecaptcha's
  // widget shouldn't fire onChange twice for the same solve, but if it ever
  // does (network jitter, the widget being torn down mid-callback), the
  // useState updates we make in onChange are batched and the second fire
  // would still see the original pendingV2Retry from closure — leading to
  // a double submit. A ref flips synchronously so the second call no-ops.
  const v2InFlight = useRef(false);

  const waitForRecaptchaScript = useCallback(
    (timeoutMs = 4000): Promise<void> =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const tick = () => {
          if (!scriptLoadingRef.current && executeV3Ref.current) {
            resolve();
            return;
          }
          if (Date.now() - start > timeoutMs) {
            reject(
              new RecaptchaV3UnavailableError(
                `script load timeout after ${timeoutMs}ms`
              )
            );
            return;
          }
          setTimeout(tick, 100);
        };
        tick();
      }),
    []
  );

  const acquireTokens = useCallback(async (): Promise<RecaptchaTokens> => {
    if (!recaptchaEnabled) {
      return {};
    }
    if (!v3SiteKey) {
      throw new RecaptchaV3UnavailableError(
        "v3SiteKey empty in runtime config"
      );
    }
    await waitForRecaptchaScript();
    const execute = executeV3Ref.current;
    if (!execute) {
      throw new RecaptchaV3UnavailableError(
        "executeV3 missing — GoogleReCaptchaProvider not mounted"
      );
    }
    // grecaptcha can throw its own errors (e.g. "reCAPTCHA Timeout (d)" when
    // its fingerprinting probe stalls — common with ad blockers or flaky
    // networks). Wrap any such failure as RecaptchaV3UnavailableError so the
    // caller's existing fallback path arms the v2 challenge instead of
    // letting the error escape as an unhandled rejection with no UI feedback.
    let token: string | null | undefined;
    try {
      token = await execute(action);
    } catch (err) {
      const reason =
        err instanceof Error
          ? `executeV3 threw: ${err.message}`
          : "executeV3 threw a non-Error value";
      throw new RecaptchaV3UnavailableError(reason);
    }
    if (!token) {
      throw new RecaptchaV3UnavailableError("executeV3 resolved empty token");
    }
    return { recaptchaToken: token };
  }, [action, recaptchaEnabled, v3SiteKey, waitForRecaptchaScript]);

  const notifyValidationFailed = useCallback(() => {
    // Surfacing a terminal error ends this attempt's retry lifecycle — clear
    // the one-shot guard so the next fresh submission gets its silent retry
    // back (covers v2 onError and the no-v2-key fallback paths too).
    v3RetryUsed.current = false;
    addNotification({
      type: "error",
      message: intl.formatMessage(messages.recaptchaValidationFailed),
    });
  }, [addNotification, intl]);

  const armV2Challenge = useCallback(
    (retry: RecaptchaRetry) => {
      if (v2SiteKey) {
        setPendingV2Retry(() => retry);
        setV2Required(true);
      } else {
        // No v2 key configured — surface a hard failure rather than silently
        // looping the user.
        notifyValidationFailed();
      }
    },
    [v2SiteKey, notifyValidationFailed]
  );

  const promoteToV2Challenge = useCallback(
    (_reason: string, retry: RecaptchaRetry): void => {
      armV2Challenge(retry);
    },
    [armV2Challenge]
  );

  const handleRecaptchaError = useCallback(
    (err: unknown, retry: RecaptchaRetry): boolean => {
      const code = getGraphQLErrorCode(err);
      if (code === ResultCodes.RECAPTCHA_RETRYABLE_FAILURE) {
        // Transient (browser-error / timeout-or-duplicate / network blip).
        // Silently retry once with a fresh v3 token; escalate to v2 if the
        // retry also fails retryably (guard already set on the second pass).
        if (v3RetryUsed.current) {
          armV2Challenge(retry);
          return true;
        }
        v3RetryUsed.current = true;
        acquireTokens()
          .then((tokens) => retry(tokens))
          .catch((retryErr) => {
            // A fresh-token acquisition failure (script/timeout) routes to v2;
            // any replayed retryable failure is handled by the guard above on
            // re-entry. Other errors are owned by the caller's error handling.
            if (retryErr instanceof RecaptchaV3UnavailableError) {
              armV2Challenge(retry);
            }
          });
        return true;
      }
      if (code === ResultCodes.RECAPTCHA_V2_REQUIRED) {
        armV2Challenge(retry);
        return true;
      }
      if (code === ResultCodes.RECAPTCHA_VALIDATION_FAILED) {
        notifyValidationFailed();
        return true;
      }
      return false;
    },
    [acquireTokens, armV2Challenge, notifyValidationFailed]
  );

  const resetV2Challenge = useCallback(() => {
    v2InFlight.current = false;
    v3RetryUsed.current = false;
    setV2Submitting(false);
    setV2Required(false);
    setPendingV2Retry(null);
    setV2CheckboxKey((k) => k + 1);
  }, []);

  const v2Challenge = useMemo<ReactElement | null>(() => {
    if (!v2Required || !v2SiteKey) return null;
    return (
      <section
        className={v2ContainerClassName ?? "recaptcha-v2"}
        data-testid={v2TestId ?? `recaptcha-v2-${action}`}
      >
        <p>{intl.formatMessage(messages.recaptchaV2ChallengePrompt)}</p>
        <GoogleReCaptchaCheckbox
          key={v2CheckboxKey}
          siteKey={v2SiteKey}
          language={currentLanguage}
          onChange={(token) => {
            if (v2InFlight.current || !token || !pendingV2Retry) return;
            v2InFlight.current = true;
            setV2Submitting(true);
            const retry = pendingV2Retry;
            // Hide the checkbox immediately so a stale token can't be
            // re-submitted; the retry owns the loading spinner from here.
            setV2Required(false);
            setPendingV2Retry(null);
            retry({ recaptchaV2Token: token })
              .catch(() => {
                // Replay surfaced a non-recaptcha error — provider-level
                // error state will already have notified the user.
              })
              .finally(() => {
                v2InFlight.current = false;
                setV2Submitting(false);
              });
          }}
          onExpired={() => {
            setV2Submitting(false);
            setV2CheckboxKey((k) => k + 1);
          }}
          onError={() => {
            notifyValidationFailed();
            v2InFlight.current = false;
            setV2Submitting(false);
            setV2Required(false);
            setPendingV2Retry(null);
            setV2CheckboxKey((k) => k + 1);
          }}
        />
      </section>
    );
  }, [
    v2Required,
    v2SiteKey,
    v2CheckboxKey,
    pendingV2Retry,
    currentLanguage,
    intl,
    action,
    v2ContainerClassName,
    v2TestId,
    notifyValidationFailed,
  ]);

  return {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    resetV2Challenge,
    v2Challenge,
    isV2ChallengeActive: v2Required || v2Submitting,
    v2Submitting,
    isEnabled: recaptchaEnabled,
    RecaptchaV3UnavailableError,
  };
};

export default useRecaptchaSubmit;
